import { ExerciseImage } from 'components/ExerciseImage';
import { StaticSetIndicator } from 'components/StaticSetIndicator';
import { SupersetChicklet } from 'components/SupersetChicklet';
import React from 'react';
import styled from 'styled-components';
import { colors } from 'styleguide/colors';
import { H5, P2, P3, P4 } from 'styleguide/Texts';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { themeState } from 'styleguide/theme';
import { View } from 'styleguide/View';
import { ExerciseInnerProps } from './types';
import { ColoredIcon } from 'styleguide/Icons';
import { prTitleForPRType } from 'utils/prs';
import { FlexRow } from 'styleguide/Row';
import { t } from 'translations/t';

const Container = styled(View)``;

const TitleContainer = styled(FlexRow)`
  gap: ${Spacing.md}px;
`;

const HeaderContainer = styled.div`
  display: flex;
  margin-top: ${Spacing.md}px;
`;

const Column1 = styled.div`
  display: flex;
  flex: 1;
  justify-content: left;
  padding-inline-start: ${Spacing.md}px;
`;

const Column2 = styled.div`
  display: flex;
  flex: 4;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

const SetContainer = styled.div`
  display: flex;
  padding: ${Spacing.sm}px 0;
  border-radius: ${CornerRadius.md}px;
`;

export const Exercise = ({
  supersetId,
  image,
  title,
  notes,
  secondColumnHeader,
  sets,
  onExerciseClick,
}: ExerciseInnerProps) => {
  return (
    <Container>
      {supersetId !== undefined && (
        <View style={{ flexDirection: 'row', marginBottom: Spacing.md }}>
          <SupersetChicklet id={supersetId} />
        </View>
      )}
      <TitleContainer
        onClick={() => onExerciseClick?.()}
        style={{ cursor: onExerciseClick ? 'pointer' : 'default' }}
      >
        <ExerciseImage source={image} diameter={48} />
        <H5>{title}</H5>
      </TitleContainer>
      {!!notes && (
        <P2 type="secondary" style={{ marginTop: Spacing.sm }}>
          {notes}
        </P2>
      )}
      <HeaderContainer>
        <Column1>
          <P4 type="secondary">{t('web.workoutDetails.exercise.columnHeader.sets')}</P4>
        </Column1>
        <Column2>
          <P4 type="secondary">{secondColumnHeader}</P4>
        </Column2>
      </HeaderContainer>
      {sets.map((s, index) => (
        <SetContainer
          key={s.key}
          style={{ backgroundColor: getSimpleSetBackgroundColor(index, themeState.current.name) }}
        >
          <Column1>
            <StaticSetIndicator indicator={s.indicator} />
          </Column1>
          <Column2>
            <FlexRow>
              <P2>{s.value}</P2>
              {s.rpe && (
                <P2 type="secondary">
                  &nbsp;{t('web.workoutDetails.exercise.atRpe', { rpe: s.rpe })}
                </P2>
              )}
            </FlexRow>
            <FlexRow style={{ gap: Spacing.md, paddingInlineEnd: Spacing.md, flexWrap: 'wrap' }}>
              {s.prs.map((pr, index) => {
                return (
                  <FlexRow style={{ gap: Spacing.xs }} key={index}>
                    <ColoredIcon type="pr" />
                    <P3 style={{ color: colors.grey }}>{prTitleForPRType(pr.type)}</P3>
                  </FlexRow>
                );
              })}
            </FlexRow>
          </Column2>
        </SetContainer>
      ))}
    </Container>
  );
};

const getSimpleSetBackgroundColor = (index: number, theme: 'dark' | 'light'): string => {
  const oddSetColor = theme === 'light' ? colors.greyUltraLight : colors.greyDark;

  return index % 2 === 0 ? colors.transparent : oddSetColor;
};
